import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { InfoCircle } from '@untitled-ui/icons-react';
import { useState } from 'react';

import { Button } from '../../../components/buttons/button';
import { FeaturedIcon } from '../../../components/featured-icon';
import { addTrackCrumb, trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { openHelpScoutChat } from '../../../services/helpscout';
import { getStores } from '../../../services/storage';
import { UIKey } from '../../../services/storage/ui-key-value';
import { tGlobal, tNamespace } from '../../../services/translation';

const t = tNamespace('app.screen.removed-org');

export const Route = createFileRoute('/app/_screen/removed-org')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/_screen/removed-org',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

type Step = 1 | 2;

function textForStep(step: Step) {
  if (step === 1) {
    return {
      title: t('title1'),
      description: t('description1'),
      leftButton: tGlobal('contactSupport'),
      rightButton: tGlobal('continue'),
    };
  }
  return {
    title: t('title2'),
    description: t('description2'),
    leftButton: t('deleteAccount'),
    rightButton: t('setupNewOrg'),
  };
}

function handleSetupNewOrg(navigate: ReturnType<typeof useNavigate>) {
  getStores().uiKeyValue.getState().setToStore(UIKey.HAS_REMOVED_ORG, false);
  navigate({ to: '/app/onboarding' }).catch(function (err) {
    captureException(
      err,
      {
        file: 'removed-org.tsx',
        method: 'navigate',
        infoMessage: 'unable to navigate to onboarding',
      },
      trackError
    );
  });
}

function RouteComponent() {
  const [step, setStep] = useState<Step>(1);
  const [deleteAccountLoading] = useState(false);
  const navigate = useNavigate();

  function handleContinueToNextStep() {
    setStep(2);
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  function handleDeleteAccount() {
    // TODO: we dont have delete account
    //
    // setDeleteAccountLoading(true);
    // // NOTE: This happen after the org deletion, if the user want to delete his account we need to pass a reason
    // //  per the backend request, so we are using the other reason and specify the reason in the extraDetails
    // getStores()
    //   .users.getState()
    //   .remove(CancelReason.Other, 'Delete Org')
    //   .then(function () {
    //     setDeleteAccountLoading(false);
    //     return getStores().users.getState().signOut();
    //   })
    //   .catch(function (err) {
    //     setDeleteAccountLoading(false);
    //     captureException(
    //       err,
    //       {
    //         file: 'removed-org.tsx',
    //         method: 'handleDeleteAccount',
    //         infoMessage: 'unable to delete account',
    //       },
    //       trackError
    //     );
    //   });
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center justify-center">
        {step == 1 ? (
          <FeaturedIcon icon={<InfoCircle />} color="gray" />
        ) : (
          <FeaturedIcon icon={<InfoCircle />} color="red" />
        )}
        <div className="my-6 flex w-[400px] flex-col items-center justify-center gap-1">
          <p className="font-display-2 text-center">{textForStep(step).title}</p>
          <p className="font-body-1-r text-center text-branding-gray">
            {textForStep(step).description}
          </p>
        </div>
        <div className="flex w-[450px] flex-row gap-4">
          <Button
            size="xl"
            variant={step === 1 ? 'outline' : 'destructive'}
            className="support-chat-button w-full"
            onClick={function (e) {
              if (step === 1) {
                e.preventDefault();
                openHelpScoutChat().catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'routes/app/_screen/removed-org.tsx',
                      method: 'contactSupport',
                      infoMessage: 'Failed to open HelpScout chat',
                    },
                    trackError
                  );
                });
              } else {
                e.preventDefault();
                handleDeleteAccount();
              }
            }}
            loading={deleteAccountLoading}
          >
            {textForStep(step).leftButton}
          </Button>
          <Button
            size="xl"
            variant="accent"
            className="w-full"
            onClick={function () {
              if (step === 1) {
                handleContinueToNextStep();
              } else {
                handleDeleteAccount();
                handleSetupNewOrg(navigate);
              }
            }}
          >
            {textForStep(step).rightButton}
          </Button>
        </div>
      </div>
    </div>
  );
}
